import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Wallet, Plus, Minus, Gamepad2, Users, Trophy,
  Copy, RefreshCw, Play, X, Clock, Dice5
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import LudoBoard from "../components/LudoBoard";

const ENTRY_FEES = [1, 5, 10, 20, 50, 100];

const safeClipboardWrite = async (text) => {
  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* ignore */ }
  return false;
};

// Web-Audio tick sound for goti movement (no asset file needed)
let _audioCtx = null;
function playTick() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 540;
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.09);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) { /* ignore */ }
}
function playCapture() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(380, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(90, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.22, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) { /* ignore */ }
}
// Dice rattle — "khadbad khadbad" tumbling noise (filtered noise bursts)
function playDiceRattle(durationMs = 700) {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const startT = ctx.currentTime;
    const endT = startT + durationMs / 1000;
    // Short noise bursts every ~55ms — feels like wooden dice hitting a surface
    let t = startT;
    const bursts = [];
    while (t < endT) {
      bursts.push(t);
      t += 0.045 + Math.random() * 0.035; // 45–80ms gaps
    }
    bursts.forEach((bt) => {
      const bufSize = Math.floor(ctx.sampleRate * 0.06);
      const buffer = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufSize; i++) {
        // Decaying noise burst
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 2.2);
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      // Band-pass filter for "wood click" tone (~900–2500 Hz range)
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 1100 + Math.random() * 1500;
      bp.Q.value = 2.5;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.22 + Math.random() * 0.08, bt);
      gain.gain.exponentialRampToValueAtTime(0.001, bt + 0.06);
      src.connect(bp).connect(gain).connect(ctx.destination);
      src.start(bt);
      src.stop(bt + 0.07);
    });
  } catch (e) { /* ignore */ }
}

// Celebratory fanfare — ascending triad + sparkle when user wins money
function playWinFanfare() {  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    // Notes: C5, E5, G5, C6 (ascending major chord)
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      const t0 = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.28, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.4);
    });
    // Sparkle shimmer tail
    const tailStart = ctx.currentTime + 0.55;
    [1318.5, 1567.98, 2093].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const t0 = tailStart + i * 0.07;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.3);
    });
  } catch (e) { /* ignore */ }
}

const GamesPage = () => {
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ usdt_balance: 0, total_won: 0, games_played: 0, games_won: 0 });
  const [depositAddress, setDepositAddress] = useState("");
  const [rooms, setRooms] = useState([]);
  const [myGames, setMyGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showJoinRoom, setShowJoinRoom] = useState(false);
  const [selectedFee, setSelectedFee] = useState(1);
  const [joinRoomId, setJoinRoomId] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [depositTxHash, setDepositTxHash] = useState("");
  const [processing, setProcessing] = useState(false);

  // Active Game (dice race)
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [diceFace, setDiceFace] = useState(1);
  const [countdown, setCountdown] = useState(0);
  const [showWinModal, setShowWinModal] = useState(false);
  const [winAmount, setWinAmount] = useState(0);
  const shownWinRoomRef = useRef(null);
  const pollRef = useRef(null);
  const lobbyPollRef = useRef(null);

  const api = authAxios();

  const fetchLobby = async () => {
    try {
      const [walletRes, addressRes, roomsRes, myGamesRes] = await Promise.all([
        api.get("/games/wallet"),
        api.get("/games/deposit-address"),
        api.get("/games/ludo/rooms"),
        api.get("/games/ludo/my-games")
      ]);
      setWallet(walletRes.data);
      setDepositAddress(addressRes.data.wallet_address);
      setRooms(roomsRes.data.rooms || []);
      setMyGames(myGamesRes.data.games || []);

      // Auto-enter if user has a playing game
      const playing = (myGamesRes.data.games || []).find(g => g.status === 'playing');
      if (playing && !activeRoomId) {
        setActiveRoomId(playing.room_id);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // Lobby polling (only when not in an active game)
  useEffect(() => {
    fetchLobby();
    if (!activeRoomId) {
      lobbyPollRef.current = setInterval(fetchLobby, 8000);
      return () => clearInterval(lobbyPollRef.current);
    }
    // eslint-disable-next-line
  }, [activeRoomId]);

  // Active game polling
  const fetchGameState = async (roomId) => {
    try {
      const res = await api.get(`/games/ludo/state/${roomId}`);
      setGameState(res.data);
      if (res.data.status === 'finished') {
        clearInterval(pollRef.current);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!activeRoomId) {
      setGameState(null);
      return;
    }
    fetchGameState(activeRoomId);
    pollRef.current = setInterval(() => fetchGameState(activeRoomId), 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [activeRoomId]);

  // Animate + rattle sound when OPPONENT rolls dice (mirror experience of user's own roll)
  const lastSeenRollRef = useRef({ roll: null, roller: null });
  useEffect(() => {
    const gs = gameState?.game_state;
    if (!gs || !user?.id) return;
    const newRoll = gs.last_roll;
    const newRoller = gs.last_roller;
    if (newRoll == null || !newRoller) return;
    const prev = lastSeenRollRef.current;
    // Detect a fresh roll different from what we last animated
    if (prev.roll === newRoll && prev.roller === newRoller) return;
    lastSeenRollRef.current = { roll: newRoll, roller: newRoller };
    // Skip if this roll belongs to ME (already animated locally via rollDice)
    if (newRoller === user.id) return;
    // Don't double-trigger if a local rolling animation is already running
    if (rolling) return;
    // Opponent rolled — kick off the same tumble + sound + settle
    setRolling(true);
    playDiceRattle(700);
    let tick = 0;
    const spin = setInterval(() => {
      setDiceFace(Math.floor(Math.random() * 6) + 1);
      tick++;
      if (tick > 7) clearInterval(spin);
    }, 80);
    setTimeout(() => {
      clearInterval(spin);
      setDiceFace(newRoll);
      setRolling(false);
    }, 750);
    // eslint-disable-next-line
  }, [gameState, user]);

  // Countdown timer for turn deadline
  useEffect(() => {
    if (!gameState?.game_state?.turn_deadline) { setCountdown(0); return; }
    const tick = () => {
      const deadline = new Date(gameState.game_state.turn_deadline).getTime();
      const now = Date.now();
      setCountdown(Math.max(0, Math.floor((deadline - now) / 1000)));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [gameState]);

  // 3-minute game countdown
  useEffect(() => {
    if (!gameState?.game_deadline) { setGameCountdown(null); return; }
    const tick = () => {
      const d = new Date(gameState.game_deadline).getTime();
      setGameCountdown(Math.max(0, Math.floor((d - Date.now()) / 1000)));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [gameState]);

  // Win celebration popup — trigger once when user wins a finished game
  useEffect(() => {
    if (!gameState || !user?.id) return;
    if (gameState.status !== 'finished') return;
    if (gameState.winner_id !== user.id) return;
    if (shownWinRoomRef.current === activeRoomId) return;
    shownWinRoomRef.current = activeRoomId;
    setWinAmount(gameState.winner_amount || 0);
    setShowWinModal(true);
    playWinFanfare();
  }, [gameState, user, activeRoomId]);

  // Animate displayTokens toward the authoritative gameState.tokens (step-by-step with tick sound)
  useEffect(() => {
    const serverTokens = gameState?.game_state?.tokens;
    if (!serverTokens) return;
    // Initialize on first view
    if (Object.keys(displayTokens).length === 0) {
      setDisplayTokens(serverTokens);
      return;
    }
    // If currently animating, don't kick another animation
    if (animLockRef.current) return;

    // Find any token whose server progress > displayed progress → animate it
    let animated = false;
    for (const pid of Object.keys(serverTokens)) {
      const serverArr = serverTokens[pid] || [];
      const localArr = displayTokens[pid] || [];
      for (let i = 0; i < serverArr.length; i++) {
        const sp = serverArr[i]?.progress ?? 0;
        const lp = localArr[i]?.progress ?? 0;
        if (sp > lp) {
          animateTokenStep(pid, i, lp, sp, serverTokens);
          animated = true;
          break;
        } else if (sp === 0 && lp > 0) {
          // Token was captured — flash to home
          playCapture();
          const next = cloneTokens(displayTokens);
          if (next[pid]?.[i]) next[pid][i].progress = 0;
          setDisplayTokens(next);
        }
      }
      if (animated) break;
    }
    if (!animated) {
      // Sync any other non-animated changes (captures, opponent stayed)
      setDisplayTokens(serverTokens);
    }
    // eslint-disable-next-line
  }, [gameState]);

  const cloneTokens = (tokens) => {
    const out = {};
    for (const [k, v] of Object.entries(tokens)) out[k] = v.map(t => ({ ...t }));
    return out;
  };

  const animateTokenStep = (pid, idx, fromProg, toProg, finalTokens) => {
    animLockRef.current = true;
    let cur = fromProg;
    const step = () => {
      cur += 1;
      const snap = cloneTokens(displayTokens);
      if (!snap[pid]) snap[pid] = [];
      if (!snap[pid][idx]) snap[pid][idx] = { progress: 0 };
      snap[pid][idx].progress = cur;
      setDisplayTokens(snap);
      playTick();
      if (cur < toProg) {
        setTimeout(step, 220);
      } else {
        // Done — finalize to server tokens (applies captures if any)
        setDisplayTokens(finalTokens);
        animLockRef.current = false;
      }
    };
    setTimeout(step, 150);
  };

  const copyAddress = async () => {
    const ok = await safeClipboardWrite(depositAddress);
    if (ok) toast.success("Address copied!");
    else toast.info("Copy failed — please copy manually");
  };

  const submitDeposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!depositTxHash || depositTxHash.length < 10) { toast.error("Enter valid transaction hash"); return; }
    setProcessing(true);
    try {
      await api.post("/games/deposit", { amount: amt, transaction_hash: depositTxHash });
      toast.success("Deposit submitted");
      setShowDeposit(false);
      setDepositAmount("");
      setDepositTxHash("");
      fetchLobby();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit deposit");
    }
    setProcessing(false);
  };

  const createRoom = async () => {
    if (wallet.usdt_balance < selectedFee) {
      toast.error("Insufficient balance!");
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post("/games/ludo/create", { entry_fee: selectedFee });
      toast.success(`Room created: ${res.data.room_id}`);
      const copied = await safeClipboardWrite(res.data.room_id);
      if (copied) toast.info("Room ID copied to clipboard!");
      setShowCreateRoom(false);
      fetchLobby();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create room");
    }
    setProcessing(false);
  };

  const joinRoom = async () => {
    if (!joinRoomId.trim()) {
      toast.error("Enter room ID");
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post("/games/ludo/join", { room_id: joinRoomId.trim().toUpperCase() });
      toast.success("Joined successfully!");
      setShowJoinRoom(false);
      setJoinRoomId("");
      setActiveRoomId(res.data.room_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to join room");
    }
    setProcessing(false);
  };

  const cancelRoom = async (roomId) => {
    try {
      await api.post(`/games/ludo/cancel`, { room_id: roomId });
      toast.success("Room cancelled, refund credited!");
      fetchLobby();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const [movableTokens, setMovableTokens] = useState([]);
  const [pendingDice, setPendingDice] = useState(null);
  // Animated tokens (mirrors gameState.tokens but moves step-by-step with sound)
  const [displayTokens, setDisplayTokens] = useState({});
  const animLockRef = useRef(false);
  // 3-min game timer
  const [gameCountdown, setGameCountdown] = useState(null);

  const rollDice = async () => {
    if (rolling) return;
    setRolling(true);
    playDiceRattle(800);
    let tick = 0;
    const spin = setInterval(() => {
      setDiceFace(Math.floor(Math.random() * 6) + 1);
      tick++;
      if (tick > 8) clearInterval(spin);
    }, 80);
    try {
      const res = await api.post("/games/ludo/roll", { room_id: activeRoomId });
      setTimeout(async () => {
        clearInterval(spin);
        setDiceFace(res.data.roll);
        if (res.data.game_over) {
          toast.success(res.data.message);
          fetchLobby();
        } else if (res.data.skipped) {
          toast.info("No valid move — turn passed");
          setPendingDice(null);
          setMovableTokens([]);
        } else if (res.data.auto_played) {
          setPendingDice(null);
          setMovableTokens([]);
        } else {
          // Multiple movable tokens — ask user to click one
          setPendingDice(res.data.roll);
          setMovableTokens(res.data.movable || []);
          toast.info("Tap a glowing token to move");
        }
        fetchGameState(activeRoomId);
        setRolling(false);
      }, 800);
    } catch (e) {
      clearInterval(spin);
      toast.error(e.response?.data?.detail || "Failed to roll");
      setRolling(false);
    }
  };

  const moveToken = async (tokenIdx) => {
    try {
      const res = await api.post("/games/ludo/move", { room_id: activeRoomId, token_index: tokenIdx });
      if (res.data.game_over) {
        toast.success(res.data.message);
        fetchLobby();
      }
      if (res.data.captures && res.data.captures.length) {
        toast.info("🎯 Capture! Opponent token sent home");
      }
      setPendingDice(null);
      setMovableTokens([]);
      fetchGameState(activeRoomId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    }
  };

  const claimTimeout = async () => {
    try {
      const res = await api.post("/games/ludo/claim-timeout", { room_id: activeRoomId });
      toast.success(res.data.message);
      fetchGameState(activeRoomId);
      fetchLobby();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot claim yet");
    }
  };

  const exitGame = () => {
    setActiveRoomId(null);
    setGameState(null);
    setDisplayTokens({});
    setMovableTokens([]);
    setPendingDice(null);
    setShowWinModal(false);
    shownWinRoomRef.current = null;
    fetchLobby();
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return toast.error("Enter valid amount");
    if (!withdrawAddress.trim()) return toast.error("Enter wallet address");
    if (parseFloat(withdrawAmount) > wallet.usdt_balance) return toast.error("Insufficient balance");
    setProcessing(true);
    try {
      await api.post("/games/withdraw", {
        amount: parseFloat(withdrawAmount),
        wallet_address: withdrawAddress.trim()
      });
      toast.success("Withdrawal submitted!");
      setShowWithdraw(false);
      setWithdrawAmount("");
      setWithdrawAddress("");
      fetchLobby();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="screen-container flex items-center justify-center">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  // ======= ACTIVE GAME VIEW =======
  if (activeRoomId && gameState) {
    const gs = gameState.game_state || {};
    const myId = user?.id;
    const slots = gameState.slots || {};
    const isCreator = myId === gameState.creator_id;
    const myName = isCreator ? gameState.creator_name : gameState.opponent_name;
    const oppName = isCreator ? gameState.opponent_name : gameState.creator_name;
    const isMyTurn = gs.current_turn === myId;
    const finished = gameState.status === 'finished';
    const iWon = finished && gameState.winner_id === myId;
    const tokens = gs.tokens || {};

    // token progress summary — use displayTokens for live decrement on capture
    const visibleTokens = (displayTokens && Object.keys(displayTokens).length) ? displayTokens : tokens;
    const myTokens = visibleTokens[myId] || [];
    const oppId = isCreator ? gameState.opponent_id : gameState.creator_id;
    const oppTokens = visibleTokens[oppId] || [];
    const myScore = myTokens.reduce((s, t) => s + (t.progress || 0), 0);
    const oppScore = oppTokens.reduce((s, t) => s + (t.progress || 0), 0);
    const myHome = myTokens.filter(t => t.progress >= 57).length;
    const oppHome = oppTokens.filter(t => t.progress >= 57).length;
    const leading = myScore > oppScore ? 'me' : oppScore > myScore ? 'opp' : 'tie';

    // Match score-strip color dots to actual token color on board (slot 0 = red, slot 1 = yellow)
    const mySlot = slots[myId] ?? (isCreator ? 0 : 1);
    const oppSlot = slots[oppId] ?? (isCreator ? 1 : 0);
    const slotDotColor = (s) => s === 0 ? 'bg-red-500' : 'bg-yellow-400';

    return (
      <div className="flex flex-col bg-background overflow-hidden" style={{ height: "100dvh", maxHeight: "100dvh" }} data-testid="ludo-game-view">
        {/* Compact top bar: back | prize | timer | room */}
        <div className="flex items-center gap-2 px-2 py-1 border-b border-border bg-black/60 shrink-0">
          <button onClick={exitGame} className="p-0.5" data-testid="exit-game-btn">
            <ChevronLeft size={20} />
          </button>
          <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-md px-1.5 py-0.5">
            <span className="text-[8px] text-yellow-300 uppercase">Prize</span>
            <span className="text-yellow-300 font-bold text-xs ml-1">${(gameState.entry_fee * 2 * 0.9).toFixed(2)}</span>
          </div>
          {!finished && gameCountdown !== null && (
            <div className={`rounded-md px-1.5 py-0.5 border ${gameCountdown < 30 ? 'bg-red-500/30 border-red-500 animate-pulse' : 'bg-black/40 border-border'}`} data-testid="game-timer">
              <span className="text-[8px] text-muted-foreground uppercase">Time</span>
              <span className={`ml-1 font-mono font-bold text-xs ${gameCountdown < 30 ? 'text-red-300' : 'text-white'}`}>
                {String(Math.floor(gameCountdown / 60))}:{String(gameCountdown % 60).padStart(2, '0')}
              </span>
            </div>
          )}
          <span className="ml-auto text-[8px] font-mono text-muted-foreground truncate max-w-[60px]">{activeRoomId}</span>
        </div>

        {/* Score strip (compact) */}
        <div className="grid grid-cols-2 gap-1 px-2 py-0.5 bg-black/40 shrink-0" data-testid="live-score">
          <div className={`rounded-md px-2 py-0.5 flex items-center justify-between ${leading === 'me' ? 'bg-green-500/25 border border-green-400' : 'bg-black/40 border border-border'}`}>
            <span className="flex items-center gap-1 min-w-0">
              <span className={`w-2 h-2 rounded-full ${slotDotColor(mySlot)} border border-white shrink-0`} data-testid="my-color-dot"></span>
              <span className="text-[10px] truncate">{myName || 'You'}</span>
            </span>
            <span className="font-bold text-xs ml-1" data-testid="my-score">{myScore}</span>
          </div>
          <div className={`rounded-md px-2 py-0.5 flex items-center justify-between ${leading === 'opp' ? 'bg-green-500/25 border border-green-400' : 'bg-black/40 border border-border'}`}>
            <span className="flex items-center gap-1 min-w-0">
              <span className={`w-2 h-2 rounded-full ${slotDotColor(oppSlot)} border border-white shrink-0`} data-testid="opp-color-dot"></span>
              <span className="text-[10px] truncate">{oppName || 'Opp'}</span>
            </span>
            <span className="font-bold text-xs ml-1" data-testid="opp-score">{oppScore}</span>
          </div>
        </div>

        {/* Board fills remaining space */}
        <div className="flex-1 flex items-center justify-center px-2 py-1 min-h-0 overflow-hidden">
          <LudoBoard
            tokens={(displayTokens && Object.keys(displayTokens).length) ? displayTokens : tokens}
            slots={slots}
            myId={myId}
            myMovable={isMyTurn ? movableTokens : []}
            onMoveToken={moveToken}
            lastRoll={gs.last_roll}
          />
        </div>

        {/* Bottom: dice + status row — stays fixed at bottom of viewport */}
        {finished ? (
          <div className={`p-2 text-center shrink-0 ${iWon ? 'bg-green-500/25' : 'bg-red-500/15'}`} data-testid="game-result">
            <Trophy size={20} className={`inline mr-1 mb-0.5 ${iWon ? 'text-yellow-400' : 'text-muted-foreground'}`} />
            <span className="font-bold text-sm">{iWon ? `🎉 You Won +$${gameState.winner_amount?.toFixed(2)}` : 'Better luck next time'}</span>
            {gameState.timeout_finish && <span className="text-[9px] ml-2 text-yellow-300">⏱ by timer</span>}
            <button onClick={exitGame} className="ml-2 bg-primary text-black px-3 py-1 rounded-md text-xs font-bold" data-testid="back-to-lobby-btn">Lobby</button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-t border-border bg-black/70 shrink-0">
            <div className="flex-1 min-w-0">
              <p className="text-[9px] text-muted-foreground uppercase leading-tight">Turn</p>
              <p className={`font-bold text-xs truncate leading-tight ${isMyTurn ? 'text-green-400' : 'text-yellow-400'}`}>
                {isMyTurn ? (pendingDice !== null ? `Rolled ${pendingDice} — tap goti` : 'Your turn') : `${oppName || 'Opp'}'s turn`}
              </p>
              {countdown > 0 && isMyTurn && (
                <p className="text-[9px] text-orange-400 flex items-center gap-1 leading-tight"><Clock size={10} />{countdown}s</p>
              )}
            </div>

            <button
              onClick={isMyTurn && pendingDice === null && !rolling ? rollDice : undefined}
              disabled={!isMyTurn || pendingDice !== null || rolling}
              className={`${isMyTurn && pendingDice === null && !rolling ? 'ring-4 ring-yellow-400/70 animate-pulse cursor-pointer' : 'opacity-60'} transition rounded-xl shrink-0`}
              data-testid="roll-dice-btn"
              aria-label="Roll dice"
            >
              <DiceBox face={diceFace} rolling={rolling} compact />
            </button>

            <div className="flex-1 text-right min-w-0">
              {!isMyTurn && countdown === 0 && gs.current_turn && gs.current_turn !== myId ? (
                <button
                  onClick={claimTimeout}
                  className="bg-orange-600 px-2 py-1 rounded text-xs font-bold"
                  data-testid="claim-timeout-btn"
                >
                  Claim Win
                </button>
              ) : (
                <>
                  <p className="text-[9px] text-muted-foreground uppercase leading-tight">Last</p>
                  <p className="font-bold text-xs text-primary leading-tight">{gs.last_roll ?? '—'}</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Win celebration modal */}
        {showWinModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm overflow-hidden"
            data-testid="win-celebration-modal"
          >
            {/* Confetti */}
            {Array.from({ length: 40 }).map((_, i) => {
              const colors = ['#fde047', '#f87171', '#34d399', '#60a5fa', '#c084fc', '#fb923c'];
              const left = Math.random() * 100;
              const delay = Math.random() * 1.5;
              const dur = 2.2 + Math.random() * 1.8;
              const bg = colors[i % colors.length];
              return (
                <span
                  key={i}
                  className="confetti-piece"
                  style={{
                    left: `${left}%`,
                    background: bg,
                    animationDuration: `${dur}s`,
                    animationDelay: `${delay}s`,
                    borderRadius: i % 3 === 0 ? '50%' : '2px',
                  }}
                />
              );
            })}

            <div className="win-modal-card relative bg-gradient-to-b from-yellow-500/20 via-black/80 to-black border-2 border-yellow-400 rounded-2xl px-8 py-10 text-center max-w-xs w-[85%] shadow-2xl">
              <Trophy size={72} className="win-trophy text-yellow-400 mx-auto mb-3" />
              <p className="text-xs uppercase tracking-[0.3em] text-yellow-300 mb-2">Victory</p>
              <h2 className="text-2xl font-bold text-white mb-3">You Won</h2>
              <p className="win-amount text-5xl font-black mb-4" data-testid="win-amount-display">
                ${winAmount.toFixed(2)}
              </p>
              <p className="text-xs text-yellow-200/80 mb-5">🎉 Credited to your game wallet</p>
              <button
                onClick={() => { setShowWinModal(false); exitGame(); }}
                className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold py-2.5 rounded-lg transition"
                data-testid="win-modal-collect-btn"
              >
                Collect & Back to Lobby
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ======= LOBBY VIEW =======
  return (
    <div className="screen-container pb-20" data-testid="games-page">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="p-2" data-testid="back-btn">
          <ChevronLeft size={24} />
        </button>
        <Gamepad2 size={24} className="text-primary" />
        <h1 className="font-bold text-xl">Games</h1>
      </div>

      <div className="p-4 space-y-4">
        {/* Game Wallet Card */}
        <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 border border-purple-500/30 rounded-2xl p-4" data-testid="game-wallet-card">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={20} className="text-purple-400" />
            <span className="text-purple-400 font-medium">Game Wallet (USDT)</span>
          </div>
          <p className="text-3xl font-bold text-white mb-4" data-testid="wallet-balance">
            ${wallet.usdt_balance.toFixed(2)} <span className="text-lg text-muted-foreground">USDT</span>
          </p>

          <div className="grid grid-cols-3 gap-2 text-center text-xs mb-4">
            <div className="bg-black/30 rounded-lg p-2">
              <p className="text-muted-foreground">Games</p>
              <p className="text-white font-bold">{wallet.games_played}</p>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <p className="text-muted-foreground">Won</p>
              <p className="text-green-400 font-bold">{wallet.games_won}</p>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <p className="text-muted-foreground">Total Won</p>
              <p className="text-yellow-400 font-bold">${wallet.total_won.toFixed(2)}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowDeposit(true)}
              className="flex-1 bg-green-600 hover:bg-green-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
              data-testid="deposit-btn"
            >
              <Plus size={18} /> Deposit
            </button>
            <button
              onClick={() => setShowWithdraw(true)}
              className="flex-1 bg-orange-600 hover:bg-orange-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
              data-testid="withdraw-btn"
            >
              <Minus size={18} /> Withdraw
            </button>
            <button
              onClick={() => navigate("/spin")}
              className="flex-1 bg-purple-600 hover:bg-purple-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
              data-testid="game-spin-btn"
            >
              <Users size={18} /> Spin
            </button>
          </div>
        </div>

        {/* Ludo Card */}
        <div className="bg-card border border-border rounded-2xl p-4" data-testid="ludo-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center text-2xl">🎲</div>
            <div>
              <h3 className="font-bold text-lg">Ludo Race</h3>
              <p className="text-xs text-muted-foreground">1v1 Dice Race • Win 90% of Pool</p>
            </div>
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            {ENTRY_FEES.map(fee => (
              <span key={fee} className="px-2 py-1 bg-primary/20 text-primary rounded-full text-xs">${fee}</span>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowCreateRoom(true)}
              className="flex-1 bg-primary hover:bg-primary/90 py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-black"
              data-testid="create-room-btn"
            >
              <Play size={18} /> Create Room
            </button>
            <button
              onClick={() => setShowJoinRoom(true)}
              className="flex-1 bg-secondary hover:bg-secondary/80 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
              data-testid="open-join-btn"
            >
              <Users size={18} /> Join Room
            </button>
          </div>
        </div>

        {/* Waiting Games (mine) */}
        {myGames.filter(g => g.status === 'waiting' && g.creator_id === user?.id).length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Clock size={18} className="text-yellow-400" /> My Waiting Rooms
            </h3>
            <div className="space-y-2">
              {myGames.filter(g => g.status === 'waiting' && g.creator_id === user?.id).map(game => (
                <div key={game.room_id} className="bg-black/30 rounded-xl p-3" data-testid={`waiting-room-${game.room_id}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-mono text-primary text-sm">{game.room_id}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">Waiting</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">Entry: ${game.entry_fee}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => { const ok = await safeClipboardWrite(game.room_id); toast.success(ok ? "Copied!" : game.room_id); }}
                      className="flex-1 bg-blue-600 py-2 rounded-lg text-sm flex items-center justify-center gap-1"
                    >
                      <Copy size={14} /> Share
                    </button>
                    <button
                      onClick={() => cancelRoom(game.room_id)}
                      className="flex-1 bg-red-600 py-2 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Available Rooms */}
        {rooms.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Users size={18} className="text-blue-400" /> Open Rooms
            </h3>
            <div className="space-y-2">
              {rooms.filter(r => r.creator_id !== user?.id).map(room => (
                <div key={room.room_id} className="bg-black/30 rounded-xl p-3 flex justify-between items-center" data-testid={`open-room-${room.room_id}`}>
                  <div>
                    <p className="font-mono text-sm">{room.room_id}</p>
                    <p className="text-xs text-muted-foreground">by {room.creator_name} • ${room.entry_fee}</p>
                  </div>
                  <button
                    onClick={() => { setJoinRoomId(room.room_id); setShowJoinRoom(true); }}
                    className="bg-primary text-black px-4 py-2 rounded-lg text-sm font-bold"
                  >
                    Join
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Wins */}
        {myGames.filter(g => g.status === 'finished' && g.winner_id === user?.id).length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Trophy size={18} className="text-yellow-400" /> Recent Wins
            </h3>
            <div className="space-y-2">
              {myGames.filter(g => g.status === 'finished' && g.winner_id === user?.id).slice(0, 5).map(game => (
                <div key={game.room_id} className="bg-black/30 rounded-xl p-3 flex justify-between items-center">
                  <div>
                    <p className="font-mono text-sm">{game.room_id}</p>
                    <p className="text-xs text-muted-foreground">${game.entry_fee} game</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-500/20 text-green-400">
                    +${game.winner_amount?.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Deposit Modal */}
      {showDeposit && (
        <>
          <div className="modal-overlay" onClick={() => setShowDeposit(false)}></div>
          <div className="modal-content" data-testid="deposit-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-xl">Deposit USDT</h2>
              <button onClick={() => setShowDeposit(false)}><X size={24} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-2">Send USDT (BEP20) to this platform deposit address:</p>
            <div className="bg-black/50 rounded-xl p-3 mb-3">
              <p className="font-mono text-xs break-all text-primary" data-testid="deposit-address-text">
                {depositAddress || "No address set"}
              </p>
            </div>
            <button
              onClick={copyAddress}
              className="w-full bg-primary text-black py-3 rounded-xl font-bold flex items-center justify-center gap-2 mb-3"
              data-testid="copy-address-btn"
            >
              <Copy size={18} /> Copy Address
            </button>

            <div className="border-t border-border pt-3 mt-2">
              <input
                type="number"
                placeholder="Amount sent (USDT)"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="input-dark mb-3"
                data-testid="deposit-amount-input"
              />
              <input
                type="text"
                placeholder="Transaction Hash (TxID)"
                value={depositTxHash}
                onChange={(e) => setDepositTxHash(e.target.value.trim())}
                className="input-dark mb-3 font-mono text-xs"
                data-testid="deposit-txhash-input"
              />
              <button
                onClick={submitDeposit}
                disabled={processing || !depositAmount || parseFloat(depositAmount) <= 0 || !depositTxHash || depositTxHash.length < 10}
                className="w-full bg-green-600 hover:bg-green-700 py-3 rounded-xl font-bold disabled:opacity-50"
                data-testid="submit-deposit-btn"
              >
                {processing ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Withdraw Modal */}
      {showWithdraw && (
        <>
          <div className="modal-overlay" onClick={() => setShowWithdraw(false)}></div>
          <div className="modal-content" data-testid="withdraw-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-xl">Withdraw USDT</h2>
              <button onClick={() => setShowWithdraw(false)}><X size={24} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-2">Available: ${wallet.usdt_balance.toFixed(2)}</p>
            <input
              type="number"
              placeholder="Amount"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="input-dark mb-3"
              data-testid="withdraw-amount"
            />
            <input
              type="text"
              placeholder="Your USDT (BEP20) Address"
              value={withdrawAddress}
              onChange={(e) => setWithdrawAddress(e.target.value)}
              className="input-dark mb-4"
              data-testid="withdraw-address"
            />
            <button
              onClick={handleWithdraw}
              disabled={processing}
              className="w-full bg-orange-600 py-3 rounded-xl font-bold disabled:opacity-50"
              data-testid="submit-withdraw-btn"
            >
              {processing ? "Processing..." : "Submit"}
            </button>
          </div>
        </>
      )}

      {/* Create Room Modal */}
      {showCreateRoom && (
        <>
          <div className="modal-overlay" onClick={() => setShowCreateRoom(false)}></div>
          <div className="modal-content" data-testid="create-room-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-xl">Create Ludo Room</h2>
              <button onClick={() => setShowCreateRoom(false)}><X size={24} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Select entry fee:</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {ENTRY_FEES.map(fee => (
                <button
                  key={fee}
                  onClick={() => setSelectedFee(fee)}
                  className={`py-3 rounded-xl font-bold ${selectedFee === fee ? 'bg-primary text-black' : 'bg-secondary'}`}
                  data-testid={`fee-${fee}`}
                >
                  ${fee}
                </button>
              ))}
            </div>
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-4">
              <p className="text-sm text-green-400">
                Winner gets: <strong>${(selectedFee * 2 * 0.9).toFixed(2)}</strong> (90% of ${selectedFee * 2} pool)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                ⏱ Waiting rooms auto-cancel & refund after 10 minutes if no one joins.
              </p>
            </div>
            <button
              onClick={createRoom}
              disabled={processing || wallet.usdt_balance < selectedFee}
              className="w-full bg-primary text-black py-3 rounded-xl font-bold disabled:opacity-50"
              data-testid="submit-create-room-btn"
            >
              {processing ? "Creating..." : `Create Room ($${selectedFee})`}
            </button>
            {wallet.usdt_balance < selectedFee && (
              <p className="text-red-400 text-xs text-center mt-2">Insufficient balance</p>
            )}
          </div>
        </>
      )}

      {/* Join Room Modal */}
      {showJoinRoom && (
        <>
          <div className="modal-overlay" onClick={() => setShowJoinRoom(false)}></div>
          <div className="modal-content" data-testid="join-room-modal">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-xl">Join Room</h2>
              <button onClick={() => setShowJoinRoom(false)}><X size={24} /></button>
            </div>
            <input
              type="text"
              placeholder="Enter Room ID (e.g., LUDO-ABC123)"
              value={joinRoomId}
              onChange={(e) => setJoinRoomId(e.target.value.toUpperCase())}
              className="input-dark mb-4 font-mono"
              data-testid="join-room-input"
            />
            <button
              onClick={joinRoom}
              disabled={processing || !joinRoomId.trim()}
              className="w-full bg-primary text-black py-3 rounded-xl font-bold disabled:opacity-50"
              data-testid="submit-join-btn"
            >
              {processing ? "Joining..." : "Join Room"}
            </button>
          </div>
        </>
      )}

      <BottomNav />
    </div>
  );
};

// ==== Sub-components ====

const DICE_PIPS = {
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [0, 2], [2, 0], [2, 2]],
  5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
  6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
};

const DiceBox = ({ face, rolling, big, compact }) => {
  const pips = DICE_PIPS[face] || DICE_PIPS[1];
  const grid = [[false, false, false], [false, false, false], [false, false, false]];
  pips.forEach(([r, c]) => { grid[r][c] = true; });
  const sizeCls = big ? "w-20 h-20 md:w-24 md:h-24 p-3" : compact ? "w-12 h-12 p-1.5" : "w-16 h-16 p-2";
  const pipSize = big ? "w-3.5 h-3.5" : compact ? "w-2 h-2" : "w-2.5 h-2.5";
  return (
    <div
      className={`${sizeCls} relative rounded-2xl grid grid-cols-3 gap-1 select-none transition-transform duration-200
        bg-gradient-to-br from-white via-gray-50 to-gray-200 border-2 border-gray-300
        ${rolling ? "dice-rolling" : "dice-settled hover:scale-110 active:scale-95"}
      `}
      style={{
        boxShadow: rolling
          ? "0 12px 24px rgba(0,0,0,0.5), inset 0 2px 6px rgba(255,255,255,0.8), inset 0 -2px 6px rgba(0,0,0,0.2)"
          : "0 6px 10px rgba(0,0,0,0.35), inset 0 2px 4px rgba(255,255,255,0.9), inset 0 -2px 3px rgba(0,0,0,0.15)"
      }}
      data-testid="dice-box"
    >
      {grid.flat().map((filled, idx) => (
        <div key={idx} className="flex items-center justify-center">
          {filled && (
            <div
              className={`${pipSize} bg-gradient-to-br from-gray-800 to-black rounded-full`}
              style={{ boxShadow: "inset 0 1px 2px rgba(255,255,255,0.3), 0 1px 1px rgba(0,0,0,0.3)" }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default GamesPage;
